/**
 * Per-rarity card counts derived from a PurchaseLot's tracked inventory.
 *
 * Differs from `rarityComposition.ts` (Studio bundle chips) by keeping each
 * `CardRarity` distinct — Holo and Reverse Holo are not collapsed, Damaged
 * is not folded into Common. The lot detail screen needs the full breakdown
 * so the user can see "I have 12 Holos and 8 Reverse Holos from this lot,"
 * not "I have 20 H".
 *
 * No schema change to PurchaseLot — the breakdown is derived live from
 * `cards.filter(c => c.sourceLotId === lot.id)`. Re-renders for free when
 * AppContext updates. No refresh button needed.
 */

import type { Card, CardRarity, PurchaseLot } from "@/context/AppContext";

export interface LotRarityCounts {
  total: number;
  byRarity: Partial<Record<CardRarity, number>>;
}

/** Stable left-to-right chip order — roughly bulk → premium → utility → edge cases. */
export const RARITY_DISPLAY_ORDER: CardRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Promo",
  "Trainer",
  "Energy",
  "Damaged",
  "Sealed",
];

export function rarityCountsForLot(
  lot: PurchaseLot,
  cards: Card[],
): LotRarityCounts {
  const byRarity: Partial<Record<CardRarity, number>> = {};
  let total = 0;
  for (const c of cards) {
    if (c.sourceLotId !== lot.id) continue;
    const qty = c.quantity ?? 0;
    if (qty <= 0) continue;
    byRarity[c.rarity] = (byRarity[c.rarity] ?? 0) + qty;
    total += qty;
  }
  return { total, byRarity };
}
